// 千日手の軍配者たち（hollow-actor の作り直し）駅1 の仮の技表と、挟み撃ちの決まりを守る段取り (2026-10-29)。
//   ラボ twins-proxy-lab.html だけが使う。配布版のボスにはまだつないでいない。
//   決まり（台帳 docs/ledger/bosses.md・研究 docs/research/2026-10-28-twin-maidens-station0-round4.md）:
//     1. 二人の当たりが SAME_BEAT_MS より近い時は、守り方（弾く／避ける）をそろえる
//     3. 二人が同時に大技を出さない。片方の大技の戻りは、もう片方が小技で埋める
//     4. 合わせ技は順番に流れる連携だけ（当たりは前後に並ぶ）
//     5. 片方が赤く光っている間（＋前後 RED_CLEAR_MS）は、相方の弾く当たりも、飛んでいる鈴も入れない（11-03 作者「赤が弾けた」の原因の推測を塞ぐ）。
//        別々の技の間は開始をずらして守る (earliestStart)。合わせ技の中では、赤く光り始めるのを相方の弾く当たりの後まで遅らせる (redGlowFrom)

export type Defense = 'parry' | 'dodge'   // dodge＝赤の攻撃（弾けない・避ける）
export type TwinId = 'ane' | 'imo'
export type HitKind = 'melee' | 'wave' | 'pass' | 'bullet'

export interface TwinHit {
  at: number          // 技の始まりから当たるまで（ms）。bullet は鈴を鳴らして弾が出る時
  by: TwinId
  defense: Defense
  kind: HitKind
  reach: number       // 当たる距離（px・体の中心から）
}

export interface TwinMove {
  id: string
  label: string
  owner: TwinId | 'both'
  tell: string        // 殴る直前の合図（殴る側の体に出す）
  big: boolean
  gapFiller: boolean  // 相方の大技の戻りを埋める小技
  hits: TwinHit[]
  endMs: number       // 戻りまで含めた長さ
  needDx: number      // 始める時の相手との距離の上限
  lungePx?: number    // 溜めの間に前へ出る距離
  slide?: boolean     // 主人公の向こう側へ抜ける
}

export const SAME_BEAT_MS = 300
/** 赤い光と、相方の弾く当たり (飛んでいる鈴を含む) の間に空ける時間 (ms・決まり5)。 */
export const RED_CLEAR_MS = 150
export const BULLET_SPEED = 380   // px/s

const h = (at: number, by: TwinId, defense: Defense, kind: HitKind, reach: number): TwinHit => ({ at, by, defense, kind, reach })

export const TWIN_MOVES: TwinMove[] = [
  // 姉（軍配・指し手）
  { id: 'a_strike', label: '軍配打ち', owner: 'ane', tell: '軍配を上げる', big: false, gapFiller: true,
    hits: [h(600, 'ane', 'parry', 'melee', 115)], endMs: 1100, needDx: 125 },
  { id: 'a_double', label: '二段払い', owner: 'ane', tell: '軍配を横に引く', big: false, gapFiller: false,
    hits: [h(550, 'ane', 'parry', 'melee', 125), h(850, 'ane', 'parry', 'melee', 125)], endMs: 1450, needDx: 135 },
  { id: 'a_lunge', label: '踏み込み突き', owner: 'ane', tell: '軍配を後ろへ引いて腰を落とす', big: false, gapFiller: false,
    hits: [h(700, 'ane', 'parry', 'melee', 110)], endMs: 1300, needDx: 270, lungePx: 160 },
  { id: 'a_order', label: '号令', owner: 'ane', tell: '軍配を高く掲げて止まる（赤）', big: true, gapFiller: false,
    hits: [h(850, 'ane', 'dodge', 'wave', 300)], endMs: 2000, needDx: 300 },
  { id: 'j_sennichite', label: '千日手（合わせ技）', owner: 'both', tell: '姉が軍配で妹を指す', big: true, gapFiller: false,
    hits: [h(700, 'ane', 'parry', 'melee', 130), h(1100, 'imo', 'parry', 'melee', 130), h(1500, 'ane', 'parry', 'melee', 130),
      h(2050, 'imo', 'dodge', 'melee', 150)], endMs: 2900, needDx: 140 },
  // 妹（鈴・駒）
  { id: 'i_bell', label: '鈴の弾', owner: 'imo', tell: '鈴を鳴らす', big: false, gapFiller: true,
    hits: [h(400, 'imo', 'parry', 'bullet', 0)], endMs: 800, needDx: 900 },
  { id: 'i_strike', label: '鈴打ち', owner: 'imo', tell: '鈴を振りかぶる', big: false, gapFiller: true,
    hits: [h(450, 'imo', 'parry', 'melee', 105)], endMs: 900, needDx: 115 },
  // 三連の間は 0.4 秒 = 2部屋目 (速さ 0.82 倍) でも 0.33 秒 = 弾ける連撃の下限 0.32 秒を割らない
  //   (10-31 作者の答え 駅6 予備点検 q2=ア「双子の三連だけ広げる」・前は 0.2 秒で、1回外すと残りの押し直しが間に合わなかった)。
  { id: 'i_triple', label: '三連打', owner: 'imo', tell: '鈴を胸に抱えて鳴らさない', big: false, gapFiller: false,
    hits: [h(500, 'imo', 'parry', 'melee', 105), h(900, 'imo', 'parry', 'melee', 105), h(1300, 'imo', 'parry', 'melee', 105)], endMs: 2000, needDx: 115 },
  { id: 'i_slide', label: '駒の滑り込み', owner: 'imo', tell: '身を低くして床に手をつく（赤）', big: true, gapFiller: false,
    hits: [h(900, 'imo', 'dodge', 'pass', 60)], endMs: 1800, needDx: 420, slide: true },
]

export const moveById = (id: string): TwinMove => {
  const m = TWIN_MOVES.find((x) => x.id === id)
  if (!m) throw new Error(`unknown twin move ${id}`)
  return m
}

/** from＝赤い当たりなら赤く光り始める時、鈴なら弾が出る時 (どちらも絶対時刻)。t との間が決まり5で見る幅。 */
export interface BookedHit { t: number; by: TwinId; defense: Defense; from?: number }

/** 技の i 番目の当たりが赤 (dodge) の時、技の始まりから何 ms で赤く光り始めるか。赤でなければ null。
 *  光るのは、その人の前の当たりの後 (無ければ技の始まり) から当たるまで。合わせ技で相方の弾く当たりがその間にある時は、
 *  その当たりの RED_CLEAR_MS 後から光る (決まり5・11-03)。 */
export function redGlowFrom(move: TwinMove, i: number): number | null {
  const x = move.hits[i]
  if (!x || x.defense !== 'dodge') return null
  let from = 0
  move.hits.forEach((y, j) => {
    if (j === i || y.at > x.at) return
    if (y.by === x.by && j < i) from = Math.max(from, y.at)
    else if (y.by !== x.by && y.defense === 'parry') from = Math.max(from, y.at + RED_CLEAR_MS)
  })
  return from
}

// 当たり時刻（絶対）。bullet は弾が届くまでの見込みを足す
export function absHits(move: TwinMove, start: number, distPx = 0): BookedHit[] {
  return move.hits.map((x, i) => {
    const t = start + x.at + (x.kind === 'bullet' ? (distPx / BULLET_SPEED) * 1000 : 0)
    const glow = redGlowFrom(move, i)
    const from = glow !== null ? start + glow : x.kind === 'bullet' ? start + x.at : undefined
    return from === undefined ? { t, by: x.by, defense: x.defense } : { t, by: x.by, defense: x.defense, from }
  })
}

// 決まり5：片方の赤く光る間 (前後 RED_CLEAR_MS) に、相方の弾く当たり (鈴は出てから届くまで) が入る組を返す
export function redGlowConflicts(hits: BookedHit[]): [BookedHit, BookedHit][] {
  const out: [BookedHit, BookedHit][] = []
  for (const r of hits) {
    if (r.defense !== 'dodge') continue
    const r0 = (r.from ?? r.t) - RED_CLEAR_MS, r1 = r.t + RED_CLEAR_MS
    for (const w of hits) {
      if (w.by === r.by || w.defense !== 'parry') continue
      if ((w.from ?? w.t) < r1 && w.t > r0) out.push([r, w])
    }
  }
  return out
}

// 決まり1：別の人の当たりが SAME_BEAT_MS 未満で並ぶのに、守り方が違う組を返す
export function beatConflicts(hits: BookedHit[]): [BookedHit, BookedHit][] {
  const out: [BookedHit, BookedHit][] = []
  const s = [...hits].sort((a, b) => a.t - b.t)
  for (let i = 0; i < s.length; i++) {
    for (let j = i + 1; j < s.length && s[j]!.t - s[i]!.t < SAME_BEAT_MS; j++) {
      if (s[i]!.by !== s[j]!.by && s[i]!.defense !== s[j]!.defense) out.push([s[i]!, s[j]!])
    }
  }
  return out
}

// 始めたい時刻 wantAt から、決まり1・3・5を破らない一番早い開始時刻を返す。
//   partnerHits＝相方が既に決めた当たり、partnerBigUntil＝相方の大技が戻り終わる時刻
export function earliestStart(move: TwinMove, wantAt: number, partnerHits: BookedHit[], partnerBigUntil: number, distPx = 0): number {
  let t = move.big ? Math.max(wantAt, partnerBigUntil) : wantAt
  for (let n = 0; n < 200; n++) {
    const mine = absHits(move, t, distPx)
    if (beatConflicts([...mine, ...partnerHits]).length === 0 && redGlowConflicts([...mine, ...partnerHits]).length === 0) return t
    t += 50
  }
  return t
}

// 相方が大技の戻りにいる時は小技で埋める（決まり3）
export function pickable(owner: TwinId, partnerInBig: boolean, partnerDown: boolean): TwinMove[] {
  return TWIN_MOVES.filter((m) => {
    if (m.owner === 'both') return !partnerDown && !partnerInBig
    if (m.owner !== owner) return false
    if (partnerInBig) return m.gapFiller
    return true
  })
}
